import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Search,
  UserPlus,
  UserMinus,
  Users,
  Phone,
  Mail,
  BadgeCheck,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MyNursesPage = () => {
  const { token } = useAuth();
  const [nurses, setNurses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchDni, setSearchDni] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newNurseForm, setNewNurseForm] = useState({
    dni: '',
    full_name: '',
    email: '',
    password: '',
    license_number: '',
    phone: ''
  });

  useEffect(() => {
    fetchMyNurses();
  }, [token]);

  const fetchMyNurses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/doctors/my-nurses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setNurses(await response.json());
      }
    } catch (error) {
      console.error('Error fetching nurses:', error);
      toast.error('Error al cargar enfermeras');
    } finally {
      setLoading(false);
    }
  };

  const searchNurseByDni = async () => {
    if (!searchDni.trim()) {
      toast.error('Ingrese un DNI para buscar');
      return;
    }
    
    setSearching(true);
    setSearchResult(null);
    setShowCreateForm(false);
    
    try {
      const response = await fetch(`${API_URL}/api/nurses/search/dni/${searchDni}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResult(data);
        
        if (!data.found) {
          setShowCreateForm(true);
          setNewNurseForm(prev => ({ ...prev, dni: searchDni }));
        }
      }
    } catch (error) {
      console.error('Error searching nurse:', error);
      toast.error('Error al buscar enfermera');
    } finally {
      setSearching(false);
    }
  };

  const assignExistingNurse = async (nurseUserId) => {
    try {
      const response = await fetch(`${API_URL}/api/doctors/my-nurses/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ nurse_user_id: nurseUserId })
      });

      if (response.ok) {
        toast.success('Enfermera asignada exitosamente');
        setDialogOpen(false);
        resetDialog();
        fetchMyNurses();
      } else {
        const responseClone = response.clone();
        try {
          const error = await response.json();
          toast.error(error.detail || 'Error al asignar enfermera');
        } catch (parseError) {
          const text = await responseClone.text();
          console.error('Response text:', text);
          toast.error('Error al asignar enfermera');
        }
      }
    } catch (error) {
      console.error('Error assigning nurse:', error);
      toast.error('Error al asignar enfermera');
    }
  };

  const createAndAssignNurse = async () => {
    if (!newNurseForm.full_name || !newNurseForm.email || !newNurseForm.password || !newNurseForm.license_number) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    if (!newNurseForm.dni) {
      toast.error('El DNI es requerido');
      return;
    }

    setCreating(true);

    try {
      const response = await fetch(`${API_URL}/api/nurses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newNurseForm)
      });

      if (response.ok) {
        toast.success('Enfermera creada y asignada exitosamente');
        setDialogOpen(false);
        resetDialog();
        fetchMyNurses();
      } else if (response.status === 409) {
        // Email or DNI already exists - try to find and assign existing nurse
        toast.info('La enfermera ya existe en el sistema. Buscando para asignarla...');

        // Search by email
        const searchResponse = await fetch(`${API_URL}/api/nurses/search/email/${encodeURIComponent(newNurseForm.email)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (searchResponse.ok) {
          const searchData = await searchResponse.json();
          if (searchData.found && searchData.nurse) {
            // Found the nurse, now assign her
            await assignExistingNurse(searchData.nurse.user_id);
          } else {
            toast.error('No se pudo encontrar la enfermera existente. Contacte al administrador.');
          }
        } else {
          toast.error('Error al buscar enfermera existente');
        }
      } else {
        const responseClone = response.clone();
        try {
          const error = await response.json();
          toast.error(error.detail || 'Error al crear enfermera');
        } catch (parseError) {
          const text = await responseClone.text();
          console.error('Response text:', text);
          toast.error('Error al crear enfermera');
        }
      }
    } catch (error) {
      console.error('Error creating nurse:', error);
      toast.error('Error al crear enfermera');
    } finally {
      setCreating(false);
    }
  };

  const unassignNurse = async (nurseUserId) => {
    if (!window.confirm('¿Está seguro de desasignar esta enfermera?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/doctors/my-nurses/${nurseUserId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Enfermera desasignada');
        fetchMyNurses();
      } else {
        toast.error('Error al desasignar enfermera');
      }
    } catch (error) {
      console.error('Error unassigning nurse:', error);
      toast.error('Error al desasignar enfermera');
    }
  };

  const resetDialog = () => {
    setSearchDni('');
    setSearchResult(null);
    setShowCreateForm(false);
    setNewNurseForm({
      dni: '',
      full_name: '',
      email: '',
      password: '',
      license_number: '',
      phone: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-nurses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis Enfermeras</h1>
          <p className="text-gray-500">Gestiona tu equipo de enfermería</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetDialog(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-nurse-button">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Enfermera
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Agregar Enfermera</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Search by DNI */}
              <div>
                <Label>Buscar por DNI</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ingrese DNI"
                    value={searchDni}
                    onChange={(e) => setSearchDni(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchNurseByDni()}
                    data-testid="search-dni-input"
                  />
                  <Button onClick={searchNurseByDni} disabled={searching} data-testid="search-dni-button">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Search Result - Nurse Found */}
              {searchResult?.found && searchResult.nurse && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{searchResult.nurse.full_name}</p>
                        <p className="text-sm text-gray-500">{searchResult.nurse.email}</p>
                        <p className="text-sm text-gray-500">DNI: {searchResult.nurse.dni}</p>
                      </div>
                      <Button 
                        onClick={() => assignExistingNurse(searchResult.nurse.user_id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="assign-existing-nurse-button"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Asignar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Create New Nurse Form */}
              {showCreateForm && (
                <div className="space-y-4 border-t pt-4">
                  <p className="text-sm text-gray-600">
                    No se encontró enfermera con DNI <strong>{searchDni}</strong>. 
                    Complete los datos para registrarla:
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Nombre Completo *</Label>
                      <Input
                        value={newNurseForm.full_name}
                        onChange={(e) => setNewNurseForm({...newNurseForm, full_name: e.target.value})}
                        placeholder="María García López"
                        data-testid="new-nurse-name"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={newNurseForm.email}
                        onChange={(e) => setNewNurseForm({...newNurseForm, email: e.target.value})}
                        placeholder="enfermera@clinia.pe"
                        data-testid="new-nurse-email"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Contraseña *</Label>
                      <Input
                        type="password"
                        value={newNurseForm.password}
                        onChange={(e) => setNewNurseForm({...newNurseForm, password: e.target.value})}
                        placeholder="••••••••"
                        data-testid="new-nurse-password"
                      />
                    </div>
                    <div>
                      <Label>N° Licencia *</Label>
                      <Input
                        value={newNurseForm.license_number}
                        onChange={(e) => setNewNurseForm({...newNurseForm, license_number: e.target.value})}
                        placeholder="ENF-12345"
                        data-testid="new-nurse-license"
                      />
                    </div>
                    <div>
                      <Label>Teléfono</Label>
                      <Input
                        value={newNurseForm.phone}
                        onChange={(e) => setNewNurseForm({...newNurseForm, phone: e.target.value})}
                        placeholder="999888777"
                        data-testid="new-nurse-phone"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={createAndAssignNurse} 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={creating}
                    data-testid="create-nurse-button"
                  >
                    {creating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando...</>
                    ) : (
                      <><UserPlus className="w-4 h-4 mr-2" /> Crear y Asignar Enfermera</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Nurses List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Mi Equipo de Enfermería ({nurses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {nurses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">No tiene enfermeras asignadas</p>
              <p className="text-sm">Haga clic en "Agregar Enfermera" para asignar una</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {nurses.map((nurse) => (
                <Card key={nurse.id} className="border hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                          <span className="text-teal-700 font-bold text-lg">
                            {nurse.full_name?.charAt(0) || 'E'}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{nurse.full_name}</h3>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <BadgeCheck className="w-3 h-3" />
                            {nurse.license_number}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => unassignNurse(nurse.user_id)}
                        data-testid={`unassign-nurse-${nurse.id}`}
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="mt-3 space-y-1 text-sm text-gray-600">
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {nurse.email}
                      </p>
                      {nurse.phone && (
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {nurse.phone}
                        </p>
                      )}
                      {nurse.dni && (
                        <p className="text-xs text-gray-400">DNI: {nurse.dni}</p>
                      )}
                    </div>
                    
                    <p className="mt-2 text-xs text-gray-400">
                      Asignada: {new Date(nurse.assigned_at).toLocaleDateString('es-PE')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyNursesPage;
